import { BaseUser, ProviderTypeEnum, UserTypeEnum } from "@vitable/asclepius-api-client-ts"
import {
  IS_PROD,
  USERS_WITH_AI_FEATURE_ACCESS,
  USERS_WITH_ASYNC_VISITS_ACCESS,
  USERS_WITH_CALENDAR_V2_ACCESS
} from "@/config"

import asclepiusConfig from "@common/config/asclepius-config"
import eventBus from "@/events/event-bus"
import globalEvents from "@/events/global-events"
import router from "@/routers"
import { useRefreshAccessToken } from "@/modules/auth/composables/auth"

interface MeState {
  token: string | undefined
  refreshToken: string | undefined
  user: BaseUser | undefined
}

const state = (): MeState => ({
  token: undefined,
  refreshToken: undefined,
  user: undefined
})

const getters = {
  allowShowAiFeatures: (state: MeState) => {
    if (!IS_PROD) return true
    return !!state && !!state.user && USERS_WITH_AI_FEATURE_ACCESS.includes(state.user.email)
  },
  allowShowAsyncVisits: (state: MeState) => {
    if (!IS_PROD) return true
    return !!state && !!state.user && USERS_WITH_ASYNC_VISITS_ACCESS.includes(state.user.email)
  },
  allowShowCalendarV2: (state: MeState) => {
    return !!state && !!state.user && USERS_WITH_CALENDAR_V2_ACCESS.includes(state.user.email)
  },
  user: (state: MeState) => {
    return state.user
  },
  token: (state: MeState) => {
    return state.token
  },
  refreshToken: (state: MeState) => {
    return state.refreshToken
  },
  userId: (state: MeState) => {
    if (!state.user) {
      return undefined
    }
    return state.user.id
  },
  providerId: (state: MeState) => {
    if (!state.user || !state.user.provider || !!state.user.clinical_admin) {
      return undefined
    }
    return state.user.provider.id
  },
  provider: (state: MeState) => {
    if (!state.user || !state.user.provider || !!state.user.clinical_admin) {
      return undefined
    }
    return state.user.provider
  },
  clinicalAdmin: (state: MeState) => {
    if (!state.user || !state.user.clinical_admin) {
      return undefined
    }
    return state.user.clinical_admin
  },
  isProvider: (state: MeState) => {
    if (!state.user) {
      return false
    }
    return state.user.user_type === UserTypeEnum.NursePractitioner
  },
  isNursePractitioner: (state: MeState) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.NursePractitioner
  },
  isPhlebotomist: (state: MeState) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.Phlebotomist
  },
  isRegisteredNurse: (state: MeState) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.RegisteredNurse
  },
  isClinicalAdmin: (state: MeState) => {
    if (!state.user) {
      return false
    }
    return !!state.user.clinical_admin
  },
  isLoggedIn: (state: MeState) => {
    return state.user && state.token && state.refreshToken
  },
  profilePictureUrl: (state: MeState, getters: any) => {
    if (!getters.isLoggedIn) throw new Error("Cannot get profile picture url for current user, no user is logged in!")
    if (state.user?.profile_picture_url?.length) return state.user.profile_picture_url
    return "/imgs/doc-1.png"
  }
}

const actions = {
  async init({ dispatch }: any) {
    const storeCache = window.localStorage.getItem("vuex")

    // Do we have a store
    if (!storeCache) return await dispatch("reset")
    const store = JSON.parse(storeCache)

    let accessToken = store.me.token
    const refreshToken = store.me.refreshToken
    if (accessToken && refreshToken) {
      // Temporary fix to remove "JWT " prefix from previously stored value
      if (accessToken.startsWith("JWT ")) {
        accessToken = accessToken.replace("JWT ", "")
        await dispatch("updateAuthTokens", { accessToken: accessToken, refreshToken: refreshToken })
      }
      asclepiusConfig.setAccessToken(accessToken)
      asclepiusConfig.setRefreshToken(refreshToken)
    } else {
      return await dispatch("reset")
    }

    // Is token valid
    await dispatch("refreshAccessToken")
  },

  async reset({ commit }: any) {
    commit("setUser", { accessToken: undefined, refreshToken: undefined, user: undefined })
  },

  async refreshAccessToken({ commit, state, dispatch }: any) {
    const { user, refreshToken, accessToken, refreshAccessToken, refreshAccessTokenResult } = useRefreshAccessToken()

    await refreshAccessToken(state.refreshToken)
    if (!refreshAccessTokenResult.value.isSuccess()) {
      return dispatch("logout")
    }

    commit("setUser", { accessToken: accessToken.value, refreshToken: refreshToken.value, user: user.value })

    eventBus.emit(globalEvents.LOG_IN)
  },

  async login({ commit }: any, payload: any) {
    const { user, token, refreshToken, postLoginRedirectPath, postLoginRedirectPageName } = payload

    // Update Store
    commit("setUser", { accessToken: token, refreshToken: refreshToken, user: user })

    // Post login redirection
    if (postLoginRedirectPath) {
      window.location.href = postLoginRedirectPath
    } else if (postLoginRedirectPageName) {
      router.push({ name: postLoginRedirectPageName })
    } else {
      router.push({ name: "appointments_calendar" })
    }

    // Emit event
    eventBus.emit(globalEvents.LOG_IN)
  },

  async updateAuthTokens({ commit }: any, payload: any) {
    const { accessToken, refreshToken } = payload
    commit("setAuthTokens", { accessToken, refreshToken })
  },

  async logout({ dispatch }: any) {
    dispatch("reset")

    if (router.currentRoute.value.path !== "/login") {
      const redirectedFromPath = router.currentRoute.value.fullPath
      router.push({ name: "login", query: { redirectedFromPath } }).then()
    }

    eventBus.emit(globalEvents.LOG_OUT)
  }
}

const mutations = {
  setUser(state: MeState, { accessToken, refreshToken, user }: any) {
    if (user?.clinical_admin) {
      user.provider = user.clinical_admin
    }

    state.token = accessToken
    state.refreshToken = refreshToken
    state.user = user

    if (refreshToken) {
      asclepiusConfig.setRefreshToken(refreshToken)
    }

    if (accessToken) {
      asclepiusConfig.setAccessToken(accessToken)
    } else {
      asclepiusConfig.clearAuthTokens()
    }
  },
  setAuthTokens(state: MeState, { accessToken, refreshToken }: any) {
    state.token = accessToken
    state.refreshToken = refreshToken

    if (refreshToken) {
      asclepiusConfig.setRefreshToken(refreshToken)
    }

    if (accessToken) {
      asclepiusConfig.setAccessToken(accessToken)
    } else {
      asclepiusConfig.clearAuthTokens()
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
